import type { ContingencyPolicy } from "./contingency";
import { DEFAULT_CONTINGENCY_POLICY, feeBearingContingency, resolveContingency } from "./contingency";
// Pure calculation functions for OpenEstimate.
// No React imports — all functions take plain data and return plain data.
// This makes the core math unit-testable independently of the component.

// ── Item traversal ────────────────────────────────────────────────────────────

export function* walkItems(rows: any[]): Generator<any> {
  for (const r of rows) {
    if (r.type === "group") yield* walkItems(r.children ?? []);
    else yield r;
  }
}

// ── Item-level helpers ────────────────────────────────────────────────────────

export const isClarification = (s: string) => s === "By Others" || s === "Excluded";

/**
 * Whether a line is money this estimate actually carries.
 *
 * Three things are not: a line switched off, an alternate nobody has
 * accepted, and a clarification -- work marked By Others or Excluded, which
 * is named on the sheet precisely so everyone can see it is somebody else's
 * to pay for.
 *
 * The rule was written out four times in this file alone, and a fifth and
 * sixth time in the Analysis tab in different words -- where the chart
 * applied none of it and called the result TOTAL, and the list under the
 * chart applied one third of it. The same estimate read $6,786,960,
 * $6,546,960 and $6,368,960 on one screen, four inches apart, with nothing
 * saying why. One definition, in one place.
 */
export const inBaseBid = (li: any): boolean =>
  !!li?.included &&
  !(li.altType && !(li.altType === "Alt" && li.altStatus === "Accepted")) &&
  !isClarification(li.costStatus ?? "");

export function getItemTotal(li: any, itemStateMap: Record<string, any>): number {
  if (li.unit === "%") {
    const base = (li.pctTargetIds ?? []).reduce((sum: number, id: string) => {
      const t = itemStateMap[id] ?? {};
      if (t.unit === "%") return sum; // prevent chaining
      return sum + (t.qty ?? 0) * (t.unitCost ?? 0);
    }, 0);
    return base * ((li.qty ?? 0) / 100);
  }
  return (li.qty ?? 0) * (li.unitCost ?? 0);
}

export function calcItemCont(
  li: any,
  projectContPct: number,
  itemStateMap: Record<string, any> = {},
): number {
  if (!li.included || li.altType) return 0;
  const base = getItemTotal(li, itemStateMap);
  const mode = li.contMode ?? "project";
  if (mode === "none") return 0;
  if (mode === "pct") return base * ((li.contValue ?? 0) / 100);
  if (mode === "dollar") return li.contValue ?? 0;
  return base * (projectContPct / 100);
}

// ── Rollup computations ───────────────────────────────────────────────────────

export type DivTotals = Record<string, { byProp: Record<string, number>; total: number }>;

export function computeDivTotals(
  divisions: any[],
  itemState: Record<string, any>,
  proposals: any[],
): DivTotals {
  const t: DivTotals = {};
  divisions.forEach((div) => {
    const byProp: Record<string, number> = {};
    let total = 0;
    div.subcategories.forEach((sub: any) =>
      [...walkItems(sub.rows)].forEach((item) => {
        const li = itemState[item.id] ?? {};
        if (!inBaseBid(li)) return;
        const amt = getItemTotal(li, itemState);
        const prop = li.proposal ?? proposals[0]?.id ?? "p1";
        byProp[prop] = (byProp[prop] ?? 0) + amt;
        total += amt;
      }),
    );
    t[div.id] = { byProp, total };
  });
  return t;
}

export function computeGrandTotal(divTotals: DivTotals): {
  byProp: Record<string, number>;
  subtotal: number;
} {
  const byProp: Record<string, number> = {};
  let subtotal = 0;
  Object.values(divTotals).forEach((d) =>
    Object.entries(d.byProp).forEach(([p, a]) => {
      byProp[p] = (byProp[p] ?? 0) + a;
      subtotal += a;
    }),
  );
  return { byProp, subtotal };
}

export function computeSubTotals(
  divisions: any[],
  itemState: Record<string, any>,
): Record<string, number> {
  const t: Record<string, number> = {};
  divisions.forEach((div) =>
    div.subcategories.forEach((sub: any) => {
      let total = 0;
      [...walkItems(sub.rows)].forEach((item) => {
        const li = itemState[item.id] ?? {};
        if (!inBaseBid(li)) return;
        total += getItemTotal(li, itemState);
      });
      t[sub.id] = total;
    }),
  );
  return t;
}

export function computeMarkupableSubtotal(
  divisions: any[],
  itemState: Record<string, any>,
): number {
  let total = 0;
  divisions.forEach((div) =>
    div.subcategories.forEach((sub: any) =>
      [...walkItems(sub.rows)].forEach((item) => {
        const li = itemState[item.id] ?? {};
        if (!inBaseBid(li)) return;
        if (!li.excludeFromMarkup) total += getItemTotal(li, itemState);
      }),
    ),
  );
  return total;
}

export function computeDivMarkupableSubtotals(
  divisions: any[],
  itemState: Record<string, any>,
): Record<string, number> {
  const m: Record<string, number> = {};
  divisions.forEach((div) => {
    let sub = 0;
    div.subcategories.forEach((s: any) =>
      [...walkItems(s.rows)].forEach((item) => {
        const li = itemState[item.id] ?? {};
        if (
          !li.included ||
          (li.altType && !(li.altType === "Alt" && li.altStatus === "Accepted")) ||
          isClarification(li.costStatus ?? "") ||
          li.excludeFromMarkup
        )
          return;
        sub += getItemTotal(li, itemState);
      }),
    );
    m[div.id] = sub;
  });
  return m;
}

export type AltTotals = {
  add: any[];
  ded: any[];
  voided: any[];
  addTotal: number;
  dedTotal: number;
  addAccepted: number;
  addTBD: number;
  dedAccepted: number;
  dedTBD: number;
};

export function computeAltTotals(
  divisions: any[],
  itemState: Record<string, any>,
): AltTotals {
  const add: any[] = [], ded: any[] = [], voided: any[] = [];
  divisions.forEach((div) =>
    div.subcategories.forEach((sub: any) =>
      [...walkItems(sub.rows)].forEach((item) => {
        const li = itemState[item.id] ?? {};
        if (!li.included) return;
        const amt = getItemTotal(li, itemState);
        const altStatus = li.altStatus ?? "TBD";
        if (li.altType === "Alt") {
          if (amt >= 0) add.push({ id: item.id, desc: li.description ?? "", amt, div: div.name, altStatus });
          else ded.push({ id: item.id, desc: li.description ?? "", amt: Math.abs(amt), div: div.name, altStatus });
        }
        if (li.altType === "Void")
          voided.push({ id: item.id, desc: li.description ?? "", amt, div: div.name, altStatus: "Void" });
      }),
    ),
  );
  const sum = (arr: any[], status: string) =>
    arr.filter((v) => v.altStatus === status).reduce((a, v) => a + v.amt, 0);
  return {
    add, ded, voided,
    addTotal: add.reduce((a, v) => a + v.amt, 0),
    dedTotal: ded.reduce((a, v) => a + v.amt, 0),
    addAccepted: sum(add, "Accepted"), addTBD: sum(add, "TBD"),
    dedAccepted: sum(ded, "Accepted"), dedTBD: sum(ded, "TBD"),
  };
}

export type ContingencyByProp = {
  byProp: Record<string, number>;
  byDivByProp: Record<string, Record<string, number>>;
  byDiv: Record<string, number>;
  total: number;
};

export function computeContingencyByProp(
  divisions: any[],
  itemState: Record<string, any>,
  contingency: number,
  proposals: any[],
): ContingencyByProp {
  const byProp: Record<string, number> = {};
  const byDivByProp: Record<string, Record<string, number>> = {};
  const byDiv: Record<string, number> = {};
  divisions.forEach((div) => {
    byDivByProp[div.id] = {};
    byDiv[div.id] = 0;
    div.subcategories.forEach((sub: any) =>
      [...walkItems(sub.rows)].forEach((item) => {
        const li = itemState[item.id] ?? {};
        // Same inclusion rule as every other rollup. A By Others or Excluded
        // line contributes nothing to any total, so it cannot carry
        // contingency either -- that would be contingency on scope nobody
        // is pricing. This was the one place that rule was missing.
        if (!inBaseBid(li)) return;
        const cont = calcItemCont(li, contingency, itemState);
        const prop = li.proposal ?? proposals[0]?.id ?? "p1";
        byProp[prop] = (byProp[prop] ?? 0) + cont;
        byDivByProp[div.id][prop] = (byDivByProp[div.id][prop] ?? 0) + cont;
        byDiv[div.id] += cont;
      }),
    );
  });
  const total = Object.values(byProp).reduce((a, v) => a + v, 0);
  return { byProp, byDivByProp, byDiv, total };
}

export type FinalTotals = {
  subtotal: number;
  /** Escalation dollars, a cost of work sitting between subtotal and markup. */
  escalation: number;
  /** The same figure as a percentage of direct cost, for labelling it. */
  escalationPct: number;
  overhead: number;
  profit: number;
  beforeCont: number;
  contingency: number;
  /** The two halves of that figure, so the totals panel can name them. */
  contingencyLine: number;
  contingencyReserve: number;
  /** The arithmetic that produced it, in order. */
  contingencySteps: import("./contingency").ContingencyStep[];
  total: number;
  costPerSF: number;
};

export function computeFinal(
  grandSubtotal: number,
  markupableSubtotal: number,
  divMarkupableSubtotals: Record<string, number>,
  divisions: any[],
  divisionMarkups: Record<string, { overhead?: number | null; profit?: number | null }>,
  overhead: number,
  profit: number,
  markupMode: string,
  contingencyTotal: number,
  blendedSF: number,
  /** How this project carries risk. Omitted means the old behaviour: every
   *  line's own buffer, summed, and nothing on top. */
  policy: ContingencyPolicy = DEFAULT_CONTINGENCY_POLICY,
  /** Per-division contingency, needed only when fee is charged on it and the
   *  divisions have markups of their own. */
  contByDiv: Record<string, number> = {},
  /** Escalation dollars, from computeEscalation. A cost of work: overhead and
   *  profit are charged on it, and a contingency reserve based on direct cost
   *  includes it. Zero means this estimate does not price escalation. */
  escalation: number = 0,
): FinalTotals {
  const s = grandSubtotal;
  const esc = Number.isFinite(escalation) ? escalation : 0;
  // Everything downstream prices the escalated cost of work, because that is
  // the cost. Charging fee on today's prices for work bought in two years
  // under-recovers by exactly the markup on the escalation.
  const work = s + esc;
  const hasCustomMarkup = Object.values(divisionMarkups).some(
    (m) => m?.overhead != null || m?.profit != null,
  );
  // Fee on contingency, where the policy asks for it, works by widening the
  // base overhead and profit are charged on rather than by adding a second
  // markup afterwards -- the two give the same dollars, but only the first
  // survives a division carrying a markup of its own.
  const feeBase = feeBearingContingency(policy, contingencyTotal);

  let oh = 0, pr = 0;
  if (hasCustomMarkup) {
    divisions.forEach((div) => {
      const dm = divisionMarkups[div.id] ?? {};
      // Escalation is spread across divisions in proportion to their
      // markupable work, so a division carrying its own markup charges that
      // markup on its own share of the escalation rather than on none of it.
      const divMk = divMarkupableSubtotals[div.id] ?? 0;
      const divEsc = markupableSubtotal > 0 ? esc * (divMk / markupableSubtotal) : 0;
      const divSub = divMk + divEsc
        + (feeBase > 0 ? (contByDiv[div.id] ?? 0) : 0);
      const effOH = dm.overhead != null ? dm.overhead : overhead;
      const effPr = dm.profit != null ? dm.profit : profit;
      if (markupMode !== "profit") oh += divSub * (effOH / 100);
      if (markupMode !== "overhead" && markupMode !== "combined") pr += divSub * (effPr / 100);
    });
    if (markupMode === "combined") pr = 0;
  } else {
    const ms = markupableSubtotal + esc + feeBase;
    oh = markupMode === "profit" ? 0 : ms * (overhead / 100);
    pr = markupMode === "overhead" || markupMode === "combined" ? 0 : ms * (profit / 100);
  }

  const bc = work + oh + pr;
  const cont = resolveContingency({
    policy, lineContingency: contingencyTotal,
    directSubtotal: work, overhead: oh, profit: pr,
  });
  const total = bc + cont.total;
  return {
    subtotal: s, escalation: esc, escalationPct: s > 0 ? (esc / s) * 100 : 0,
    overhead: oh, profit: pr, beforeCont: bc,
    contingency: cont.total,
    contingencyLine: cont.lineTotal,
    contingencyReserve: cont.reserve,
    contingencySteps: cont.steps,
    total,
    costPerSF: blendedSF > 0 ? total / blendedSF : 0,
  };
}
